import { api } from "./api";

export interface QuizQuestion {
  _id: string;
  question: string;
  options: string[];
  correctAnswer: number;
  points?: number;
}

export interface Quiz {
  _id: string;
  module: {
    _id: string;
    title: string;
  };
  course: {
    _id: string;
    title: string;
  };
  title: string;
  description?: string;
  questions: QuizQuestion[];
  timeLimit: number;
  passingScore: number;
  totalPoints?: number;
}

export interface QuizSubmission {
  quizId: string;
  answers: Record<string, number>;
  submittedAt: string;
  score?: number;
  passed?: boolean;
  timeSpent?: number;
}

export interface QuizResult {
  _id: string;
  user: string;
  quiz: {
    _id: string;
    title: string;
  };
  score: number;
  totalPoints: number;
  passed: boolean;
  submittedAt: string;
  answers?: Record<string, number>;
}

export interface QuizCertificate {
  _id: string;
  user: string;
  course: {
    _id: string;
    title: string;
    thumbnail?: string;
    category?: string;
  };
  certificateId: string;
  issuedAt: string;
  expiresAt?: string;
  verificationUrl: string;
  pdfUrl?: string;
  metadata: {
    grade?: number;
    completionDate?: string;
    quizId?: string;
    quizTitle?: string;
  };
  isVerified: boolean;
  downloaded: boolean;
  shared: boolean;
}

export interface QuizSubmitResponse {
  score: {
    earned: number;
    total: number;
    percentage: number;
  };
  passed: boolean;
  passingScore: number;
  results: any[];
  isCertificateExam: boolean;
  certificate?: {
    id: string;
    certificateId: string;
    verificationUrl: string;
  };
}

export interface ModuleQuizResult {
  quizId: string;
  quizTitle: string;
  totalPoints: number;
  passingScore: number;
  score: number;
  percentage: number;
  passed: boolean;
  attempts: number;
  completedAt: string;
}

export interface EnrolledQuiz {
  _id: string;
  title: string;
  description?: string;
  module: {
    _id: string;
    title: string;
  };
  course: {
    _id: string;
    title: string;
    thumbnail?: string;
    instructorName?: string;
  };
  timeLimit: number;
  passingScore: number;
  attemptsAllowed?: number;
  hasAttempted?: boolean;
  lastScore?: number;
  passed?: boolean;
  attempts?: number;
}

export const quizApi = {
  getModuleQuiz: (moduleId: string) => {
    return api.get<{ success: boolean; data: Quiz }>(
      `/quizzes/${moduleId}/quiz`,
    );
  },

  getQuizById: (quizId: string) => {
    return api.get<{ success: boolean; data: Quiz }>(`/quizzes/id/${quizId}`);
  },

  submitQuiz: (
    quizId: string,
    answers: Record<string, string>,
    timeSpent: number,
  ) => {
    return api.post<{ success: boolean; data: QuizSubmitResponse }>(
      `/quizzes/${quizId}/submit`,
      { answers, timeSpent },
    );
  },

  getMyQuizResults: () => {
    return api.get<{ success: boolean; data: QuizResult[] }>(
      "/quizzes/my/results",
    );
  },

  getQuizResult: (quizId: string) => {
    return api.get<{ success: boolean; data: QuizResult }>(
      `/quizzes/${quizId}/result`,
    );
  },

  getModuleQuizResult: (moduleId: string) => {
    return api.get<{ success: boolean; data: ModuleQuizResult }>(
      `/quizzes/module/${moduleId}/result`,
    );
  },

  getMyCertificates: () => {
    return api.get<{ success: boolean; data: QuizCertificate[] }>(
      "/quizzes/my/certificates",
    );
  },

  getCourseQuizzes: (courseId: string) => {
    return api.get<{
      success: boolean;
      data: Array<{ _id: string; title: string; isCertificateExam?: boolean }>;
    }>(`/quizzes/course/${courseId}`);
  },

  getEnrolledQuizzes: () => {
    return api.get<{ success: boolean; data: EnrolledQuiz[] }>(
      "/quizzes/enrolled",
    );
  },

  startQuiz: (quizId: string) => {
    return api.post<{ success: boolean; data: Quiz }>(
      `/quizzes/${quizId}/start`,
      {},
    );
  },
  
  getAllMyQuizzes: () => {
    return api.get<{ success: boolean; data: EnrolledQuiz[] }>(
      "/quizzes/my/all-quizzes",
    );
  },

  getCourseQuizzesWithCompletion: (courseId: string) => {
    return api.get(`/quizzes/course/${courseId}/my-quizzes`);
  },

  generatePrepQuestions: (quizId: string) => {
    return api.post<{
      success: boolean;
      data: {
        sessionId: string;
        questions: Array<{
          questionText: string;
          options: string[];
          correctIndex: number;
          explanation: string;
          source: string;
        }>;
        totalQuestions: number;
      };
    }>(`/quizzes/${quizId}/ai-prep/generate`, {});
  },

  submitPrepAnswer: (quizId: string, questionIndex: number, selectedIndex: number) => {
    return api.post<{
      success: boolean;
      data: {
        isCorrect: boolean;
        correctIndex: number;
        explanation: string;
        score: { earned: number; total: number; percentage: number };
        progress: { answered: number; total: number };
        completed: boolean;
      };
    }>(`/quizzes/${quizId}/ai-prep/answer`, {
      questionIndex,
      selectedIndex,
    });
  },

  getActivePrepSession: (quizId: string) => {
    return api.get<{
      success: boolean;
      data: {
        sessionId: string;
        questions: any[];
        answers: any[];
        score: any;
        progress: { answered: number; total: number };
        completed: boolean;
      } | null;
    }>(`/quizzes/${quizId}/ai-prep/session`);
  },
};